import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, PenLine } from 'lucide-react';
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found. Please check your email link.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const res = await axios.post(`${API}/auth/verify-email`, { token });
        setStatus('success');
        setMessage(res.data.message);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <PenLine className="w-8 h-8 text-brand-accent" />
          <span className="font-serif text-2xl font-semibold text-brand-primary">Collectively Write</span>
        </Link>

        <div className="bg-white rounded-2xl p-8 shadow-soft-md border border-brand-primary/10">
          {status === 'verifying' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 animate-spin text-brand-primary mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-semibold text-text-primary mb-2">Verifying your email...</h1>
              <p className="text-text-secondary">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8" data-testid="verify-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-semibold text-text-primary mb-2">Email Verified!</h1>
              <p className="text-text-secondary mb-6">{message}</p>
              <Link to="/login">
                <Button className="btn-primary h-12 px-8" data-testid="go-to-login-btn">
                  Back to sign in
                </Button>
              </Link>
              <p className="text-xs text-text-muted mt-6">You won't be able to sign in until an admin approves your account. Please watch your email — we'll let you know as soon as you're in.</p>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8" data-testid="verify-error">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-semibold text-text-primary mb-2">Verification Failed</h1>
              <p className="text-text-secondary mb-6">{message}</p>
              <div className="flex flex-col gap-3">
                <Link to="/login">
                  <Button className="w-full btn-primary h-11" data-testid="back-to-login-btn">Back to Login</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
